/**
 * Story 13.1 — the post-signup landing guard.
 *
 * The harness answers signup with a `redirect_to` (the Chief of Staff's chat
 * for an owner still in first run, `/dashboards` for everyone else). The SPA
 * follows it only when it is an in-app path: a single leading `/`, never `//`
 * (protocol-relative — another origin) and never a `/\` that a browser may
 * normalise to one, and nothing carrying a control character or whitespace
 * (which a URL parser strips). Anything else — missing, empty, absolute URL — lands home.
 */

export const DEFAULT_LANDING_PATH = '/dashboards';

export function safeRedirectPath(value: unknown): string {
  if (typeof value !== 'string') return DEFAULT_LANDING_PATH;
  // URL parsers strip tab/CR/LF (and browsers tolerate other whitespace), so
  // `/\t/evil.example` would become `//evil.example`. Refuse any ASCII
  // control character or whitespace outright.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\s]/.test(value)) return DEFAULT_LANDING_PATH;
  if (!value.startsWith('/')) return DEFAULT_LANDING_PATH;
  const second = value.charAt(1);
  if (second === '/' || second === '\\') return DEFAULT_LANDING_PATH;
  return value;
}

/**
 * Story 30.1 (ruling O15) — where to send someone back to: a push tap now,
 * and the `?next=` return path after sign-in (Story 30.2). It is
 * `safeRedirectPath` plus a refusal of the sign-in pages themselves, so a
 * return path can never loop back into `/login`, `/signup`,
 * `/forgot-password` or `/reset-password` (with any query or hash).
 *
 * The pathname is NORMALISED before the lookup, the way a router would read
 * it: dot-segments resolved (`/a/../login`, `/./login`, backslashes),
 * percent-decoded (`/%6Cogin`; an undecodable escape is refused outright),
 * repeated and trailing slashes collapsed, lower-cased. An accepted value is
 * returned as given, never the normalised form.
 */
const AUTH_PATHS = new Set(['/login', '/signup', '/forgot-password', '/reset-password']);

function normalisedPathname(path: string): string | null {
  let pathname: string;
  try {
    pathname = decodeURIComponent(new URL(path, 'http://x').pathname);
  } catch {
    return null;
  }
  return pathname.replace(/\/+/g, '/').replace(/\/+$/, '').toLowerCase() || '/';
}

export function safeReturnPath(value: unknown): string {
  const path = safeRedirectPath(value);
  if (path === DEFAULT_LANDING_PATH) return path;
  const pathname = normalisedPathname(path);
  if (pathname === null || AUTH_PATHS.has(pathname)) return DEFAULT_LANDING_PATH;
  return path;
}
